import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { CalculatorForm } from "@/components/CalculatorForm";
import { ResultsDisplay } from "@/components/ResultsDisplay";
import { calculateCompatibility } from "@/utils/calculator";
import SponsorPlacement from "@/components/SponsorPlacement";
import EmailCapture from "@/components/EmailCapture";
import { canonicalUrl } from "@/seo/routeMeta";
import { encodeFemaleParams, decodeFemaleParams, FemaleFormData } from "@/utils/shareParams";
import { trackEvent, probabilityBucket } from "@/lib/analytics";

interface FemaleCalculatorSectionProps {
  /** Route the page lives at, used to build shareable result URLs. */
  sharePath: string;
}

/**
 * The female calculator (form + results + post-result CTAs) with shared-link
 * support. Used on the homepage and the keyword landing pages so the
 * calculate/share/analytics logic lives in one place.
 */
export const FemaleCalculatorSection = ({ sharePath }: FemaleCalculatorSectionProps) => {
  const location = useLocation();

  // Shared-link support: ?age=22-35&body=not-overweight... prefills and auto-calculates.
  const sharedFormData = useMemo(
    () => decodeFemaleParams(location.search),
    [location.search]
  );

  const [results, setResults] = useState<ReturnType<typeof calculateCompatibility> | null>(null);
  const [shareUrl, setShareUrl] = useState<string>("");

  const runCalculation = (formData: FemaleFormData, source: "form" | "shared_link") => {
    const calculationResults = calculateCompatibility(formData);
    setResults(calculationResults);

    const query = encodeFemaleParams(formData);
    setShareUrl(`${canonicalUrl(sharePath)}?${query}`);
    window.history.replaceState(null, "", `${window.location.pathname}?${query}`);

    trackEvent("calculation_completed", {
      calculator: "women",
      probability_bucket: probabilityBucket(calculationResults.probability),
      source,
    });
  };

  useEffect(() => {
    if (sharedFormData) {
      runCalculation(sharedFormData, "shared_link");
    }
    // Run once on mount for shared links only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <CalculatorForm
        key={sharedFormData ? "shared" : "default"}
        initialData={sharedFormData ?? undefined}
        onCalculate={(data) => runCalculation(data, "form")}
      />

      {results && (
        <ResultsDisplay {...results} noun="women" shareUrl={shareUrl} />
      )}

      {results && <SponsorPlacement placement="post_results_female" />}
      {results && <EmailCapture />}
    </>
  );
};
